/** Browser-style tab strip in the title bar; drag to reorder (@dnd-kit), middle-click / ⌘W to close. */
import type { PageNode } from "@bundle/contracts";
import type { DocsCache, ShellState, Tab, WorkspaceCache } from "@bundle/core";
import { Avatar, Icon, useT } from "@bundle/ui";
import { ContextMenu } from "radix-ui";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import { tabInfo } from "./tabInfo.ts";

type Props = {
  state: ShellState;
  cache: WorkspaceCache | undefined;
  /** Page titles and icons for page/database tabs. */
  docs?: DocsCache;
  knownPages?: Record<string, PageNode>;
  onActivate: (id: string) => void;
  onClose: (id: string) => void;
  onPin: (id: string, pinned: boolean) => void;
  onMove: (id: string, index: number) => void;
  shownTabIds: Set<string>;
};

function TabButton({ tab, index, p }: { tab: Tab; index: number; p: Props }) {
  const t = useT();
  const { ref, isDragging } = useSortable({ id: tab.id, index, group: "tabs" });
  const info = tabInfo(tab.url, p.cache, t, p.docs, p.knownPages);
  const active = tab.id === p.state.activeTabId;
  const shown = p.shownTabIds.has(tab.id);
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
      <div
        ref={ref}
        role="tab"
        aria-selected={active}
        tabIndex={0}
        title={info.title}
        data-testid={`tab-${tab.id}`}
        className={["tab", active && "tab-active", shown && !active && "tab-shown", tab.pinned && "tab-pinned", isDragging && "tab-dragging"].filter(Boolean).join(" ")}
        onClick={() => p.onActivate(tab.id)}
        onAuxClick={(e) => {
          if (e.button === 1) p.onClose(tab.id);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") p.onActivate(tab.id);
        }}
      >
        <span className="tab-icon">
          {info.emoji !== undefined ? (
            <span className="tab-emoji">{info.emoji}</span>
          ) : info.avatar === undefined ? (
            <Icon name={info.icon} size="icon.sm" />
          ) : (
            <Avatar name={info.avatar.name} square={info.avatar.square} size="avatar.xs" />
          )}
        </span>
        {tab.pinned ? null : <span className="tab-title">{info.title}</span>}
        {tab.pinned ? null : (
          <button
            type="button"
            className="tab-close"
            aria-label={t("shell.closeTab")}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              p.onClose(tab.id);
            }}
          >
            <Icon name="close" size="icon.sm" />
          </button>
        )}
      </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="b-menu">
          <ContextMenu.Item className="b-menu-item" onSelect={() => p.onPin(tab.id, !tab.pinned)}>
            <Icon name="pin" size="icon.sm" />
            <span>{tab.pinned ? t("shell.unpinTab") : t("shell.pinTab")}</span>
          </ContextMenu.Item>
          <ContextMenu.Item className="b-menu-item" onSelect={() => p.onClose(tab.id)}>
            <Icon name="close" size="icon.sm" />
            <span>{t("shell.closeTab")}</span>
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

export function TabStrip(p: Props) {
  return (
    <DragDropProvider
      onDragEnd={(event) => {
        if (event.canceled) return;
        const source = event.operation.source;
        if (source !== null && isSortable(source) && source.index !== source.initialIndex) p.onMove(String(source.id), source.index);
      }}
    >
      <div className="tabs" role="tablist">
        {p.state.tabs.map((tab, i) => (
          <TabButton key={tab.id} tab={tab} index={i} p={p} />
        ))}
      </div>
    </DragDropProvider>
  );
}
